import {
  AttachmentImage,
  AttachmentVideo,
} from "@/components/ui/attachment-media";
import { useState } from "react";
import { isTauri } from "@/lib/is-tauri";
import { AttachmentSyncButton } from "@/components/ui/attachment-sync-button";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { Button, ImageLightbox } from "@/components/ui";
import {
  attachmentFormat,
  attachmentIsImage,
  attachmentIsVideo,
} from "@/lib/composer-attachment";

export interface ImageAttachment {
  id: string;
  url: string;
  uploading?: boolean;
  imageId?: string; // Durable UUID, set after local registration or upload
  width?: number;
  height?: number;
  filename?: string;
  contentType?: string;
  annotationNumbers?: number[];
  uploadProgress?: number;
  uploadError?: string;
}

interface ImagePreviewStripProps {
  images: ImageAttachment[];
  onRemove: (id: string) => void;
  className?: string;
  /**
   * Renders without the standalone card chrome (border/bg/blur) for use
   * inside an existing glass surface, e.g. the input bar shell.
   */
  inset?: boolean;
}

export function ImagePreviewStrip({
  images,
  onRemove,
  className,
  inset = false,
}: ImagePreviewStripProps) {
  const t = useT(inputBarMessages);
  const [previewId, setPreviewId] = useState<string | null>(null);
  if (images.length === 0) return null;
  const previewImage = images.find((img) => img.id === previewId);
  const browserImages = !isTauri() && images.some(attachmentIsImage);

  return (
    <div
      className={cn(
        "min-w-0",
        // pt-2/px-2 keep the -top-1.5/-right-1.5 remove button clear of
        // the shell's overflow:hidden boundary.
        inset
          ? "px-2 pb-1 pt-2"
          : "mb-1 rounded-preview border border-glass-stroke-subtle px-3 py-2 backdrop-blur",
        className,
      )}
      data-testid="image-preview-strip"
    >
      <div className="scrollbar-none flex items-center gap-2 overflow-x-auto">
        {images.map((img) => (
          <div
            key={img.id}
            className="group relative flex shrink-0 items-center gap-2 rounded-preview border border-glass-stroke-subtle p-1 pr-3"
            data-testid={`image-preview-${img.id}`}
          >
            {attachmentIsVideo(img) ? (
              <AttachmentVideo
                src={img.url}
                controls
                preload="metadata"
                className="h-24 max-w-48 rounded-preview"
                aria-label={`Play ${img.filename || "video"}`}
                data-testid={`video-preview-${img.id}`}
              />
            ) : attachmentIsImage(img) ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setPreviewId(img.id)}
                className="h-auto gap-2 rounded-preview p-0 text-left font-normal"
                aria-label={`Preview ${img.filename || "image"}`}
                data-testid={`image-preview-open-${img.id}`}
              >
                <AttachmentImage
                  // Server returns a relative /api/v1/uploads/... URL — must be
                  // resolved against the API host in the bundled desktop webview
                  // (same idiom as image-frame.tsx).
                  src={img.url}
                  alt={img.filename || t("attachedImage")}
                  className={cn(
                    "h-10 w-10 rounded-preview object-cover",
                    img.uploading && "opacity-60",
                  )}
                />
                <AttachmentLabel attachment={img} />
              </Button>
            ) : (
              <>
                <Icons.FileText
                  className="h-10 w-10 p-2 text-muted"
                  aria-hidden="true"
                />
                <AttachmentLabel attachment={img} />
              </>
            )}
            {browserImages &&
            !img.uploading &&
            img.imageId &&
            attachmentIsImage(img) ? (
              <AttachmentSyncButton
                key={img.url}
                src={img.url}
                testId={`image-preview-sync-${img.id}`}
              />
            ) : null}
            {img.annotationNumbers?.length ? (
              <span
                className="absolute bottom-0 left-0 right-0 truncate rounded-b-preview bg-black/75 px-1 text-center text-appkit-mini text-white"
                data-testid={`image-preview-annotations-${img.id}`}
                aria-label={`Annotations ${img.annotationNumbers.join(", ")}`}
              >
                {img.annotationNumbers.join(" · ")}
              </span>
            ) : null}
            {attachmentIsVideo(img) ? (
              <AttachmentLabel attachment={img} />
            ) : null}
            {img.uploading && !attachmentIsVideo(img) && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-preview">
                <Icons.Spinner className="h-4 w-4 animate-spin text-muted" />
              </div>
            )}
            <Button
              type="button"
              onClick={() => onRemove(img.id)}
              variant="ghost"
              size="iconXs"
              className="absolute -right-1.5 -top-1.5 h-5 w-5 rounded-full border border-glass-stroke-subtle text-muted opacity-0 backdrop-blur transition-opacity hover:text-primary focus-visible:opacity-100 group-hover:opacity-100"
              aria-label={`Remove ${img.filename || "image"}`}
              data-testid={`image-preview-remove-${img.id}`}
            >
              <Icons.X className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>
      {browserImages ? (
        <p
          className="px-1 pt-1 text-appkit-mini text-muted"
          data-testid="browser-image-sync-hint"
        >
          {t("browserImageSyncHint")}
        </p>
      ) : null}
      {previewImage ? (
        <ImageLightbox
          src={previewImage.url}
          alt={previewImage.filename || t("attachedImage")}
          onClose={() => setPreviewId(null)}
          onRemove={() => {
            setPreviewId(null);
            onRemove(previewImage.id);
          }}
        />
      ) : null}
    </div>
  );
}

function AttachmentLabel({ attachment }: { attachment: ImageAttachment }) {
  const t = useT(inputBarMessages);
  return (
    <div className="min-w-0 max-w-36">
      <div
        className="truncate text-appkit-caption text-primary"
        title={attachment.filename}
      >
        {attachment.filename || t("screenshot")}
      </div>
      <div className="text-appkit-mini text-muted">
        {attachmentFormat(attachment)}
        {attachment.uploading
          ? attachmentIsImage(attachment)
            ? t("preparingImage")
            : attachment.uploadProgress === undefined
              ? t("uploading")
              : t("uploadingPercent", { percent: attachment.uploadProgress })
          : ""}
        {attachment.uploadError ? t("uploadFailed") : ""}
      </div>
      {attachment.uploadError ? (
        <div role="alert" className="text-appkit-mini text-muted">
          {attachment.uploadError}
        </div>
      ) : null}
    </div>
  );
}
